import { fromJS, List, Map } from 'immutable'
import _get from 'lodash/get'
import _set from 'lodash/set'
import _startCase from 'lodash/startCase'
import {
  isSeries,
  isEpisode,
  isSubtypeYogaFitnessMeditation,
  CONTENT_IMAGE_TYPE_HEADSHOT,
  formatAssets,
  getFormattedContentData,
  isGaiaPlusFeature,
  getFeatureMedia,
} from '../../services/content/index'
import {
  TYPE_CONTENT_COURSE,
  TYPE_CONTENT_COURSE_MODULE,
  TYPE_DISPLAY_GUIDE,
  TYPE_DISPLAY_EVENT,
  TYPE_DISPLAY_YOGA,
  TYPE_DISPLAY_FITNESS,
  TYPE_DISPLAY_RECIPE,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_EPISODE,
  TYPE_CONTENT_SERIES,
  SUBTYPE_CONTENT_MEDITATION,
} from '../../services/content-type/index'
import {
  parse as parseQuery,
  stringify as stringifyQuery,
} from '../../services/query-string/index'
import { format, getDateLocale } from '../../services/date-time/index'
import {
  PLAY_CLICK_GA4_EVENT,
  RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { getLocalStorage, removeLocalStorage } from '../../services/local-preferences/index'
import { createRelative } from '../../services/url/index'
import { STORE_KEY_DETAIL_FEATURED_EPISODE } from '../../services/store-keys/index'
import {
  createUpstreamContext,
  SCREEN_TYPE_DETAIL_SERIES,
  SCREEN_TYPE_DETAIL_VIDEO,
  CONTEXT_TYPE_VIDEO_ENTITY,
} from '../../services/upstream-context/index'
import { get as getConfig } from '../../config/index'

export const MOBILE_WIDTH = 768

/**
 * Detail page templates types
 * Naming is based on the following pattern:
 * prefix - DETAIL_TYPE
 * contentType - VIDEO, SERIES, GUIDE, GUIDE_DAY (EPISODE is used if VIDEO is a SERIES episode)
 * displayType - normal, yoga, meditation, fitness, event, guide
 */
export const DETAIL_TYPE_VIDEO_NORMAL = 'detail-video-normal'
export const DETAIL_TYPE_SERIES_NORMAL = 'detail-series-normal'
export const DETAIL_TYPE_EPISODE_NORMAL = 'detail-episode-normal'
export const DETAIL_TYPE_VIDEO_YOGA = 'detail-video-yoga'
export const DETAIL_TYPE_SERIES_YOGA = 'detail-series-yoga'
export const DETAIL_TYPE_EPISODE_YOGA = 'detail-episode-yoga'
export const DETAIL_TYPE_VIDEO_FITNESS = 'detail-video-fitness'
export const DETAIL_TYPE_SERIES_FITNESS = 'detail-series-fitness'
export const DETAIL_TYPE_EPISODE_FITNESS = 'detail-episode-fitness'
export const DETAIL_TYPE_VIDEO_RECIPE = 'detail-video-recipe'
export const DETAIL_TYPE_SERIES_RECIPE = 'detail-series-recipe'
export const DETAIL_TYPE_EPISODE_RECIPE = 'detail-episode-recipe'
export const DETAIL_TYPE_SERIES_EVENT = 'detail-series-event'
export const DETAIL_TYPE_EPISODE_EVENT = 'detail-episode-event'
export const DETAIL_TYPE_SERIES_GUIDE = 'detail-series-guide'
export const DETAIL_TYPE_EPISODE_GUIDE = 'detail-episode-guide'
export const DETAIL_TYPE_COURSE = 'detail-course'
export const DETAIL_TYPE_COURSE_MODULE = 'detail-course-module'

/**
 * Content references types
 */

export const CONTENT_REFERENCE_TYPE_INTERVIEW_HOST = 'interview-host'
export const CONTENT_REFERENCE_TYPE_YOGA_TEACHER = 'yoga-teacher'
export const CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER = 'meditation-teacher'
export const CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR = 'fitness-instructor'
export const CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE = 'course-module-practice'
export const CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER = 'course-module-other'
export const CONTENT_REFERENCE_TYPE_CENTERS = 'centers'

/**
 * Content featured types
 */
export const CONTENT_FEATURED_TYPE_FIRST = 'first'
export const CONTENT_FEATURED_TYPE_NEXT = 'next'
export const CONTENT_FEATURED_TYPE_LAST = 'last'
export const CONTENT_FEATURED_TYPE_CONTINUE = 'continue'
export const CONTENT_FEATURED_TYPE_LATEST = 'latest'

/**
 * @param {Map} data - Detail page content data
 * @returns {String} The detail page type
 */
export function getDetailType (data) {
  const contentType = data.get('contentType')
  const displayType = data.get('displayType')

  if (contentType === TYPE_CONTENT_COURSE) {
    return DETAIL_TYPE_COURSE
  }

  if (contentType === TYPE_CONTENT_COURSE_MODULE) {
    return DETAIL_TYPE_COURSE_MODULE
  }

  if (isSeries({ contentType })) {
    switch (displayType) {
      case TYPE_DISPLAY_GUIDE:
        return DETAIL_TYPE_SERIES_GUIDE
      case TYPE_DISPLAY_EVENT:
        return DETAIL_TYPE_SERIES_EVENT
      case TYPE_DISPLAY_YOGA:
        return DETAIL_TYPE_SERIES_YOGA
      case TYPE_DISPLAY_FITNESS:
        return DETAIL_TYPE_SERIES_FITNESS
      case TYPE_DISPLAY_RECIPE:
        return DETAIL_TYPE_SERIES_RECIPE
      default:
        return DETAIL_TYPE_SERIES_NORMAL
    }
  }

  if (isEpisode({ contentType, data })) {
    switch (displayType) {
      case TYPE_DISPLAY_GUIDE:
        return DETAIL_TYPE_EPISODE_GUIDE
      case TYPE_DISPLAY_EVENT:
        return DETAIL_TYPE_EPISODE_EVENT
      case TYPE_DISPLAY_YOGA:
        return DETAIL_TYPE_EPISODE_YOGA
      case TYPE_DISPLAY_FITNESS:
        return DETAIL_TYPE_EPISODE_FITNESS
      case TYPE_DISPLAY_RECIPE:
        return DETAIL_TYPE_EPISODE_RECIPE
      default:
        return DETAIL_TYPE_EPISODE_NORMAL
    }
  }

  switch (displayType) {
    case TYPE_DISPLAY_YOGA:
      return DETAIL_TYPE_VIDEO_YOGA
    case TYPE_DISPLAY_FITNESS:
      return DETAIL_TYPE_VIDEO_FITNESS
    case TYPE_DISPLAY_RECIPE:
      return DETAIL_TYPE_VIDEO_RECIPE
    default:
      return DETAIL_TYPE_VIDEO_NORMAL
  }
}

/**
 * @param {String} text The text to truncate
 * @param {Number} approxSize The trancated result lenght (characters number)
 * @returns {String} The detail page type
 */
export function getTruncatedText (text, approxSize = 250) {
  if (!text) {
    return ''
  }

  if (text.length <= approxSize) {
    return text
  }

  const words = text.split(' ')
  let result = ''

  while (words.length && result.length < approxSize) {
    const word = words.shift()
    result += `${word} `
  }
  return result
}

/**
 * @param {string} contentType - The content type identifier
 * @param {Object} detailData - The detail data object
 * @returns {string} The UI element label for the detail page
 */
export function getDetailPageUiElementGQL (contentType, detailData) {
  if (isSeries({ contentType })) {
    return 'Series Info'
  }
  if (isEpisode({ contentType, data: detailData })) {
    return 'Episode Info'
  }

  return 'Video Info'
}

/**
 * @param {number} [size=4] - The number of placeholder items to generate
 * @returns {Immutable.List} Immutable list of placeholder objects with titleGroupId
 */
export function getPlaceholderList (size = 4) {
  return fromJS(new Array(size).fill(null).map((_, i) => ({
    titleGroupId: `${i + 1}-video`,
  })))
}

/**
 * @param {List} data - The list of course modules
 * @returns {List} List of course resources data
 */
export function getCourseResourcesData (data) {
  const courseResources = data.reduce((acc, item) => {
    const courseDayAssets = formatAssets(item.get('contentAssetReferences'))
    acc.push(...courseDayAssets.toJS())
    const stepItems = item.get('contentReferencesTo', List()).map((reference) => {
      return reference.getIn(['contentFrom', 0], Map())
    })
    stepItems.forEach((stepItem) => {
      const stepAssets = formatAssets(stepItem.get('contentAssetReferences'))
      acc.push(...stepAssets.toJS())
    })
    return acc
  }, [])
  return fromJS(courseResources)
}

/**
 * @param {List} data - The list of course steps
 * @returns {List} List of course practices data
 */
export function getCoursePracticesData (data) {
  const coursePractices = data.reduce((acc, item) => {
    const practices = item.get('contentReferencesTo', List())
      .filter((ref) => ref.getIn(['contentReferenceType', 'type']) === CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE)
      .map((ref) => ref.getIn(['contentFrom', 0], Map()))
      .filter((content) => content.size > 0)
    acc.push(...practices.toJS())
    return acc
  }, [])
  return fromJS(coursePractices)
}

/**
 * @param {Object} data - The content data object containing course references
 * @returns {Object} Object containing course modules, course resources, and course practices lists
 */
export function getCourseTabsData (data) {
  const referencesList = fromJS(_get(data, ['contentList', 'content', 0, 'contentReferencesTo'], []))
  const courseProgressData = fromJS(_get(data, ['contentList', 'content', 0, 'courseProgress'], Map()))
  const courseModulesList = referencesList
    .map((ref) => ref.getIn(['contentFrom', 0], Map()))
    .filter((module) => module.size > 0)
  const courseResourcesData = getCourseResourcesData(courseModulesList)
  const coursePracticesData = getCoursePracticesData(courseModulesList)
  return {
    courseModulesData: courseModulesList,
    courseResourcesData,
    coursePracticesData,
    courseProgressData,
  }
}

/**
 * @param {List} modulesData - The list of course modules data
 * @returns {Map|undefined} The featured content data
 */
export function getCourseFeaturedContent (modulesData, nextContentId) {
  const moduleItems = modulesData
    .flatMap((module) => module.get('contentReferencesTo', List()))
    .map((reference) => reference.getIn(['contentFrom', 0], Map()))

  if (!nextContentId) {
    return getFormattedContentData(moduleItems.get(0))
  }

  const featuredContent = moduleItems.find((content) => {
    return content.get('contentId') === nextContentId
  })
  return featuredContent
    ? getFormattedContentData(featuredContent)
    : getFormattedContentData(moduleItems.get(0))
}

/**
 * Generates optimized image URLs with fallback support for different formats and sizes
 *
 * @param {string} imageUrl - The original image URL to optimize
 * @param {number} imageWidth - The desired width of the optimized image in pixels
 * @param {number} imageHeight - The desired height of the optimized image in pixels
 *
 * @returns {Object} An object containing the optimized image URLs
 * @returns {string} returns.fallbackImageUrl - JPEG format image URL for fallback support
 * @returns {string} returns.imageUrl - WebP format image URL for modern browsers
 *
 * @throws {Error} If the imageUrl is not a valid URL, returns the original URL for both properties
 */
export function getMetaImageUrls (imageUrl, imageWidth, imageHeight) {
  try {
    const fallbackImageUrlObject = new URL(imageUrl)
    const imageUrlObject = new URL(imageUrl)
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: imageWidth,
      h: imageHeight,
      fm: 'jpg',
      compress: 'auto',
    }
    const imageOptions = {
      ...parsedImageQuery,
      w: imageWidth,
      h: imageHeight,
      fm: 'webp',
      q: '90',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedImageQuery = stringifyQuery(imageOptions)

    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)
    _set(imageUrlObject, 'search', stringifiedImageQuery)

    return {
      fallbackImageUrl: fallbackImageUrlObject.toString(),
      imageUrl: imageUrlObject.toString(),
    }
  } catch (e) {
    return {
      fallbackImageUrl: imageUrl,
      imageUrl,
    }
  }
}

/**
 * @param {Object} detailData - The detail data object containing content type and subtype
 * @returns {string} The text key for the host meta item ('guide', 'teacher', or 'host')
 */
export function getHostMetaItemTextKey (detailData) {
  const contentType = detailData.get('contentType')
  const contentSubtype = detailData.get('contentSubtype')
  if (contentType === TYPE_CONTENT_COURSE || contentType === TYPE_CONTENT_COURSE_MODULE) {
    return 'guide'
  }
  if (isSubtypeYogaFitnessMeditation({ contentSubtype })) {
    return 'teacher'
  }
  return 'host'
}

/**
 * @param {Object} detailData - The detail data object containing display type, subtype, and
 * references
 * @returns {Immutable.List} Filtered list of host references based on content type
 */
export function getHostReferences (detailData) {
  const displayType = detailData.get('displayType')
  const contentType = detailData.get('contentType')
  const contentSubtype = detailData.get('contentSubtype')
  const references = detailData.get('references', List()) || List()

  if (contentType === TYPE_CONTENT_COURSE || contentType === TYPE_CONTENT_COURSE_MODULE) {
    return references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_INTERVIEW_HOST)
  }

  if (contentSubtype === SUBTYPE_CONTENT_MEDITATION) {
    return references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER)
  }

  switch (displayType) {
    case TYPE_DISPLAY_YOGA:
      return references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_YOGA_TEACHER)
    case TYPE_DISPLAY_FITNESS:
      return references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR)
    default:
      return references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_INTERVIEW_HOST)
  }
}

/**
 * @param {Object} detailData - The detail data object containing instructor and references
 * @returns {Object|null} Host meta item object with textKey, items, and imageUrls, or null if
 * no hosts
 */
export function getHostMetaItem (detailData) {
  const hostReferences = getHostReferences(detailData)

  const hosts = hostReferences.map((hostReference) => {
    const images = hostReference.get('images', List()) || List()
    const imageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_HEADSHOT) || Map()
    const imageUrl = imageData.get('url', '')
    const imageWidth = '150'
    const imageHeight = '150'

    return {
      title: hostReference.get('title'),
      path: hostReference.get('path'),
      imageUrls: imageUrl ? getMetaImageUrls(imageUrl, imageWidth, imageHeight) : null,
    }
  }).toJS()

  if (hosts.length === 0) {
    const hostsString = detailData.get('instructor', '')
    const hostsFromString = hostsString ? hostsString.split(',')
      .map((h) => ({
        title: h.trim(),
      }))
      .filter((h) => {
        return !hosts.some((host) => host.title === h.title)
      }) : []

    hosts.push(...hostsFromString)
  }

  return hosts.length > 0 ? fromJS({
    textKey: getHostMetaItemTextKey(detailData),
    items: hosts,
    imageUrls: hosts[0].imageUrls,
  }) : null
}

export function getThemesMetaItem (detailData) {
  const contentType = detailData.get('contentType')
  const references = detailData.get('references', List()) || List()
  if (contentType === TYPE_CONTENT_COURSE) {
    const centersReferences = references.filter((r) => r.get('type') === CONTENT_REFERENCE_TYPE_CENTERS)
    const centers = centersReferences.map((center) => fromJS({
      title: center.get('title'),
      path: center.get('path'),
    }))
    return {
      textKey: 'themes',
      items: centers,
      imageUrls: null,
    }
  }
  return null
}

/**
 * Get ui location param
 * @returns {String}
 */
export function getDetailPageUiLocation (type) {
  return _startCase(`${type} detail page`)
}

/**
 * @param {Object} detailData - The detail data object containing instructor and cast information
 * @returns {Immutable.List} List of meta items including hosts and cast members
 */
export function getMetaItems (detailData) {
  const metaItems = []
  const host = getHostMetaItem(detailData)
  if (host) {
    metaItems.push(host)
  }
  const castString = detailData.get('cast', '')
  const cast = castString ? castString.split(',').map((c) => ({
    title: c.trim(),
  })) : []

  if (cast.length > 0) {
    metaItems.push({
      textKey: 'featuring',
      items: cast,
      imageUrls: null,
    })
  }

  const theme = getThemesMetaItem(detailData)

  if (theme) {
    metaItems.push(theme)
  }

  return fromJS(metaItems)
}

/**
 * Optimizes the image URLs for the detail page jumbotron and course page jumbotron
 * @param {Object} props - The props
 * @param {string} props.url - The landscape image URL
 * @param {string} props.portraitUrl - The portrait image URL
 * @returns {Object} The optimized image URLs
 * @returns {string} returns.fallbackImageUrl - The fallback image URL
 * @returns {string} returns.smallImageUrl - The small image URL
 * @returns {string} returns.mediumSmallImageUrl - The medium small image URL
 * @returns {string} returns.mediumImageUrl - The medium image URL
 * @returns {string} returns.largeImageUrl - The large image URL
 * @returns {string} returns.xLargeImageUrl - The xLarge image URL
 */
export function getOptimizedJumbotronImages (props) {
  const { url } = props
  let { portraitUrl } = props
  let smallImageUrl = ''
  let mediumSmallImageUrl = ''
  let mediumImageUrl = ''
  let largeImageUrl = ''
  let xLargeImageUrl = ''
  let fallbackImageUrl = ''

  if (!url) {
    return {
      fallbackImageUrl,
      smallImageUrl,
      mediumSmallImageUrl,
      mediumImageUrl,
      largeImageUrl,
      xLargeImageUrl,
    }
  }

  if (!portraitUrl) {
    portraitUrl = url
  }

  try {
    const fallbackImageUrlObject = new URL(url)
    const imageUrlObject = new URL(url)
    const portraitUrlObject = new URL(portraitUrl)
    const smallImageUrlObject = new URL(portraitUrl)
    const mediumSmallImageUrlObject = new URL(portraitUrl)
    const mediumImageUrlObject = new URL(url)
    const largeImageUrlObject = new URL(url)
    const xLargeImageUrlObject = new URL(url)
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const parsedPortraitImageQuery = parseQuery(_get(portraitUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: '900',
      fm: 'jpg',
      compress: 'auto',
    }
    const smallImageOptions = {
      ...parsedPortraitImageQuery,
      w: '480',
      fm: 'webp',
      q: '90',
    }
    const mediumSmallImageOptions = {
      ...parsedPortraitImageQuery,
      w: '608',
      fm: 'webp',
      q: '90',
    }
    const mediumImageOptions = {
      ...parsedImageQuery,
      w: '720',
      fm: 'webp',
      q: '90',
    }
    const largeImageOptions = {
      ...parsedImageQuery,
      w: '1200',
      fm: 'webp',
      q: '90',
    }
    const xLargeImageOptions = {
      ...parsedImageQuery,
      w: '1600',
      fm: 'webp',
      q: '90',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedSmallImageQuery = stringifyQuery(smallImageOptions)
    const stringifiedMediumSmallImageQuery = stringifyQuery(mediumSmallImageOptions)
    const stringifiedMediumImageQuery = stringifyQuery(mediumImageOptions)
    const stringifiedLargeImageQuery = stringifyQuery(largeImageOptions)
    const stringifiedXLargeImageQuery = stringifyQuery(xLargeImageOptions)

    // set the search string back onto the fallback URL
    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

    // set the search string back onto the image URLs
    _set(smallImageUrlObject, 'search', stringifiedSmallImageQuery)
    _set(mediumSmallImageUrlObject, 'search', stringifiedMediumSmallImageQuery)
    _set(mediumImageUrlObject, 'search', stringifiedMediumImageQuery)
    _set(largeImageUrlObject, 'search', stringifiedLargeImageQuery)
    _set(xLargeImageUrlObject, 'search', stringifiedXLargeImageQuery)

    fallbackImageUrl = fallbackImageUrlObject.toString()
    smallImageUrl = smallImageUrlObject.toString()
    mediumSmallImageUrl = mediumSmallImageUrlObject.toString()
    mediumImageUrl = mediumImageUrlObject.toString()
    largeImageUrl = largeImageUrlObject.toString()
    xLargeImageUrl = xLargeImageUrlObject.toString()
  } catch (e) {
    fallbackImageUrl = url
    smallImageUrl = url
    mediumSmallImageUrl = url
    mediumImageUrl = url
    largeImageUrl = url
    xLargeImageUrl = url
  }

  return {
    fallbackImageUrl,
    smallImageUrl,
    mediumSmallImageUrl,
    mediumImageUrl,
    largeImageUrl,
    xLargeImageUrl,
  }
}

/**
 * Optimizes an image URL for the episode item
 * @param {string} imageUrl - The original image URL to optimize
 * @param {number} imageWidth - The desired width of the optimized image in pixels
 * @returns {string} The optimized image URL
 */
export function getOptimizedImageUrl (imageUrl, imageWidth) {
  try {
    const fallbackImageUrlObject = new URL(imageUrl)
    const imageUrlObject = new URL(imageUrl)
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: imageWidth,
      fm: 'jpg',
      compress: 'auto',
    }
    const imageOptions = {
      ...parsedImageQuery,
      w: imageWidth,
      fm: 'webp',
      q: '100',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedImageQuery = stringifyQuery(imageOptions)

    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)
    _set(imageUrlObject, 'search', stringifiedImageQuery)

    return {
      fallbackImageUrl: fallbackImageUrlObject.toString(),
      imageUrl: imageUrlObject.toString(),
    }
  } catch (e) {
    return {
      fallbackImageUrl: imageUrl,
      imageUrl,
    }
  }
}

/**
 * Calculates progress bar values for each course step based on total progress.
 *
 * @param {Immutable.List} modulesData - List of course modules.
 * @returns {Array<number>} Array of progress percentages for each step.
 */
export function calculateProgressBarValues (modulesData) {
  if (modulesData.size === 0) {
    return []
  }

  return modulesData.map((module) => {
    const totalItems = module.getIn(['courseModuleProgress', 'metrics', 'totalItems'], 0) || 0
    const completedItems = module.getIn(['courseModuleProgress', 'metrics', 'completedItems'], 0) || 0
    if (totalItems === 0) {
      return 0
    }
    return Math.round(((completedItems / totalItems) + Number.EPSILON) * 100)
  }).toArray()
}

/**
 * @param {Object} module - The course module data
 * @returns {List} List of course module items data
 */
export function getCourseModuleItemsData (module) {
  return module.get('contentReferencesTo', List())
    .filter((reference) => !!reference.getIn(['contentFrom', 0]))
    .map((reference) => fromJS({
      item: reference.getIn(['contentFrom', 0], Map()),
      label: reference.get('label'),
    }))
}

/**
 * @param {Object} module - The course module data
 * @param {string} userLanguage - The user language
 * @param {Object} staticText - The static text object
 * @returns {string} The course module title
 */
export function getCourseModuleTitle (module, userLanguage, staticText) {
  const title = module.get('title')
  const publishedAtDate = module.get('publishedAtDate')

  if (!publishedAtDate) {
    return title
  }

  if (new Date(publishedAtDate) > new Date()) {
    const formattedPublishedAtDate = format(publishedAtDate, getDateLocale(userLanguage), 'MMMM D')
    return `${title} - ${staticText.get('comingOnDate').replace(/\$\{onDate\}/g, formattedPublishedAtDate)}`
  }

  return title
}

/**
 * Determines whether to show winback buttons (user retention)
 * Winback buttons are shown when:
 * - User doesn't have watch access
 * - Upgrade button should not be shown
 * - Content has valid feature media
 *
 * @param {Object} props - Component properties
 * @param {Map} props.detailData - Detailed content data
 * @param {boolean} props.isEvent - Whether this is an event page
 * @param {boolean} props.hasLiveAccess - Whether user has live access
 * @param {boolean} props.isGuideDetailPage - Whether this is a guide page
 * @param {boolean} props.isWinbackFlowActive - Whether the winback flow is active
* @returns {boolean|null} true if buttons should be shown, false if not,
 * null if no feature media
 */
export function shouldShowWinbackButton (props) {
  const {
    detailData,
    isEvent,
    hasLiveAccess,
    isGuideDetailPage,
    isWinbackFlowActive,
  } = props

  const contentType = detailData.get('contentType')
  const feature = getFeatureMedia({ data: detailData, contentType })

  // If no valid feature media, don't show buttons
  if (!Map.isMap(feature) || feature.size === 0) {
    return false
  }

  // Check if user already has watch access
  const hasWatchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  if (hasWatchAccess) {
    return false
  }

  // Check if upgrade button should be shown instead of winback
  const isGaiaPlusOnly = isGaiaPlusFeature(feature)
  const isGuideOrEvent = isEvent || isGuideDetailPage
  const shouldShowUpgradeButton = !hasLiveAccess && isGaiaPlusOnly && isGuideOrEvent
  if (shouldShowUpgradeButton) {
    return false
  }

  // Show winback buttons if user doesn't have access and shouldn't see upgrade
  return isWinbackFlowActive
}

export function handleSignUpClicked (props) {
  const { setEventDefault, sendOptimizelyFeatureEvent } = props
  setEventDefault(RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT.mergeDeep({
    ui_element: 'Jumbotron',
  }))
  sendOptimizelyFeatureEvent({ eventName: 'restart_subscription_clicked' })
}

// Get the node type (which is derived based on data)
export function getNodeType (props) {
  const { data } = props
  const contentType = data.get('contentType')
  const isEpisodeType = isEpisode({ contentType, data })
  const isSeriesType = isSeries({ contentType })
  let nodeType = TYPE_CONTENT_VIDEO

  if (isEpisodeType) {
    nodeType = TYPE_CONTENT_EPISODE
  }

  if (isSeriesType) {
    nodeType = TYPE_CONTENT_SERIES
  }

  return nodeType
}

/**
 * Build the upstream context for the watch events
 * @param {Object} props - The props
 * @returns {Map} updated upstream context with data from the video, series and MH
 */
export function buildJumbotronUpstreamContext (props) {
  const {
    auth,
    nodeData,
    parentNodeData,
    ga4EventData,
    upstreamContext,
  } = props
  const nodeType = getNodeType({ data: parentNodeData })
  const isSeriesNode = nodeType === TYPE_CONTENT_SERIES

  const mhTileData = Map.isMap(upstreamContext) ? upstreamContext.getIn(['data', 'mhTileData'], Map()) : Map()
  const recommId = mhTileData.get('recommId')
  const storeKey = isSeriesNode ? STORE_KEY_DETAIL_FEATURED_EPISODE : SCREEN_TYPE_DETAIL_VIDEO
  const screenType = isSeriesNode ? SCREEN_TYPE_DETAIL_SERIES : CONTEXT_TYPE_VIDEO_ENTITY
  const screenParam = isSeriesNode ? parentNodeData.get('contentId') || parentNodeData.get('id') : nodeData.getIn(['contentId']) || nodeData.getIn(['id'])
  const path = isSeriesNode ? parentNodeData.get('path') : nodeData.getIn(['path'])

  // GA4 events
  let playClickEventData = PLAY_CLICK_GA4_EVENT
    .mergeDeep({
      ...ga4EventData,
      content_type: nodeType,
      media_id: nodeData.getIn(['featureMedia', 'mediaId'], 0) || nodeData.getIn(['feature', 'id'], 0),
      media_language: parentNodeData.getIn(['primaryLanguage']) || parentNodeData.getIn(['contentLanguage']),
      is_featured_media: true,
      ui_element: 'Detail Page Primary',
    })

  // Add MH tile data if available
  if (Map.isMap(mhTileData) && mhTileData.size > 0) {
    const clickAttribution = mhTileData.get('clickAttribution') || null
    const spotlightType = mhTileData.get('spotlightType') || null
    const spotlightCampaign = mhTileData.get('spotlightCampaign') || null

    playClickEventData = playClickEventData.mergeDeep({
      row_placement: mhTileData.get('rowPlacement'),
      tile_number: mhTileData.get('tileNumber'),
      row_name: mhTileData.get('rowName'),
      ...(recommId && { recomm_id: recommId }),
      ...(clickAttribution && { click_attribution: clickAttribution }),
      ...(spotlightType && { spotlight_type: spotlightType }),
      ...(spotlightCampaign && { spotlight_campaign: spotlightCampaign }),
    })
  }

  // retrieve ai video from local storage
  const aiVideo = getLocalStorage('ai_video_clicked', auth.get('uid'), auth)
  const isAiRecommendation = aiVideo ? createRelative(path) === createRelative(aiVideo.path || '') : false
  let aiData = Map()
  if (isAiRecommendation) {
    // remove ai video from local storage
    removeLocalStorage('ai_video_clicked')
    aiData = aiData.mergeDeep({
      clickAttribution: 'AI Recommendation',
      isAiRecommendation: true,
    })
  }

  // Create the upstream context
  return createUpstreamContext({
    storeKey,
    screenType,
    screenParam,
    playEventData: mhTileData.size > 0 ? mhTileData : aiData,
    playClickEventData,
    contentId: screenParam,
  })
}

export function getWatchButtonText ({
  viewportWidth,
  staticText,
  featuredContent,
  isSeriesDetailPage,
}) {
  if (viewportWidth >= MOBILE_WIDTH && isSeriesDetailPage && featuredContent.size > 0) {
    const featuredType = featuredContent.get('featuredType')
    switch (featuredType) {
      case CONTENT_FEATURED_TYPE_FIRST:
        return staticText.get('watchFirstEpisode')
      case CONTENT_FEATURED_TYPE_NEXT:
        return staticText.get('watchNextEpisode')
      case CONTENT_FEATURED_TYPE_LAST:
        return staticText.get('watchLastEpisode')
      case CONTENT_FEATURED_TYPE_CONTINUE:
        return staticText.get('continueWatching')
      case CONTENT_FEATURED_TYPE_LATEST:
        return staticText.get('watchLatestEpisode')
      default:
        return staticText.get('watchNow')
    }
  }
  return staticText.get('watchNow')
}

/**
 * Created as a course but can not have a progress.
 * 246625 - Starcast Weekly with Richard James
 */
const config = getConfig()
const coursesWithoutProgress = _get(config, ['features', 'coursesWithoutProgress'], [])

export function checkIfCourseWithoutProgress (courseContentId) {
  return coursesWithoutProgress.includes(parseInt(courseContentId, 10))
}
